/**
 * Phase 3 D-03: pragmatic RRULE subset (NOT full RFC 5545).
 * Server-side rrule.js never imported here — this file ships shape +
 * canonical-string conversion that runs in both apps/api and apps/web
 * (the form builder uses fromRRuleString to render the current rule).
 */
import { z } from 'zod';

export const RecurrenceBuilder = z.discriminatedUnion('type', [
  z.object({ type: z.literal('once') }),
  z.object({
    type: z.literal('daily'),
    interval: z.number().int().min(1).max(30).optional(),
    until: z.iso.datetime().optional(),
  }),
  z.object({
    type: z.literal('weekdays'),
    days: z.array(z.number().int().min(1).max(7)).min(1),
    until: z.iso.datetime().optional(),
  }),
  z.object({
    type: z.literal('weekly'),
    weekday: z.number().int().min(1).max(7),
    interval: z.number().int().min(1).max(12).optional(),
    until: z.iso.datetime().optional(),
  }),
  z.object({
    type: z.literal('monthly'),
    dayOfMonth: z.number().int().min(1).max(31),
    until: z.iso.datetime().optional(),
  }),
]);
export type RecurrenceBuilder = z.infer<typeof RecurrenceBuilder>;

/**
 * Convert a builder shape to the canonical RRULE:FREQ=... fragment.
 * Day-of-week mapping: 1=MO, 2=TU, 3=WE, 4=TH, 5=FR, 6=SA, 7=SU (ISO 8601).
 * Returns null for {type:'once'} (no RRULE row in DB).
 * NOTE: never includes DTSTART; that lives in events.starts_at and is
 * composed at expansion time per PITFALLS Pitfall 5.
 */
export function toRRuleString(b: RecurrenceBuilder): string | null {
  const ISO_TO_RRULE_DAY = ['', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;
  switch (b.type) {
    case 'once': return null;
    case 'daily': {
      const parts = ['FREQ=DAILY'];
      if (b.interval && b.interval > 1) parts.push(`INTERVAL=${b.interval}`);
      if (b.until) parts.push(`UNTIL=${b.until.replace(/[-:]/g, '').replace(/\.\d+/, '')}`);
      return `RRULE:${parts.join(';')}`;
    }
    case 'weekdays': {
      const days = b.days.map((d) => ISO_TO_RRULE_DAY[d]).join(',');
      const parts = [`FREQ=WEEKLY`, `BYDAY=${days}`];
      if (b.until) parts.push(`UNTIL=${b.until.replace(/[-:]/g, '').replace(/\.\d+/, '')}`);
      return `RRULE:${parts.join(';')}`;
    }
    case 'weekly': {
      const parts = [`FREQ=WEEKLY`, `BYDAY=${ISO_TO_RRULE_DAY[b.weekday]}`];
      if (b.interval && b.interval > 1) parts.push(`INTERVAL=${b.interval}`);
      if (b.until) parts.push(`UNTIL=${b.until.replace(/[-:]/g, '').replace(/\.\d+/, '')}`);
      return `RRULE:${parts.join(';')}`;
    }
    case 'monthly': {
      const parts = [`FREQ=MONTHLY`, `BYMONTHDAY=${b.dayOfMonth}`];
      if (b.until) parts.push(`UNTIL=${b.until.replace(/[-:]/g, '').replace(/\.\d+/, '')}`);
      return `RRULE:${parts.join(';')}`;
    }
  }
}

/**
 * Parse a canonical RRULE:FREQ=... fragment back into a builder shape.
 * Returns null if the fragment uses disallowed v1 features (COUNT,
 * BYSETPOS, BYYEARDAY, FREQ=YEARLY) — caller treats null as "unsupported,
 * leave the field empty in the form".
 */
export function fromRRuleString(s: string): RecurrenceBuilder | null {
  // Strip leading "RRULE:" if present
  const body = s.replace(/^RRULE:/, '');
  const parts = Object.fromEntries(
    body.split(';').map((p) => {
      const [k, v] = p.split('=');
      return [k, v];
    }),
  ) as Record<string, string>;

  // Disallowed
  if ('COUNT' in parts || 'BYSETPOS' in parts || 'BYYEARDAY' in parts) return null;
  if (parts.FREQ === 'YEARLY') return null;

  const RRULE_TO_ISO: Record<string, number> = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 };

  const parseUntil = (u?: string): string | undefined => {
    if (!u) return undefined;
    // Reverse of toRRuleString: 20260630T235959Z -> 2026-06-30T23:59:59.000Z
    // Always emit .000Z so the result round-trips through z.iso.datetime() and
    // deep-equals the original builder value (which uses .000Z precision).
    const m = u.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/);
    if (!m) return undefined;
    const tz = m[7] === 'Z' ? '.000Z' : m[7];
    return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}${tz}`;
  };

  const interval = parts.INTERVAL ? Number(parts.INTERVAL) : undefined;
  const until = parseUntil(parts.UNTIL);

  if (parts.FREQ === 'DAILY') {
    return { type: 'daily', ...(interval ? { interval } : {}), ...(until ? { until } : {}) };
  }
  if (parts.FREQ === 'WEEKLY') {
    const days = (parts.BYDAY ?? '').split(',').map((d) => RRULE_TO_ISO[d]).filter((n): n is number => typeof n === 'number' && n > 0);
    if (days.length > 1) {
      // Multi-day weekly = 'weekdays' shape
      return { type: 'weekdays', days, ...(until ? { until } : {}) };
    }
    if (days.length === 1 && days[0] !== undefined) {
      return { type: 'weekly', weekday: days[0], ...(interval ? { interval } : {}), ...(until ? { until } : {}) };
    }
    return null;
  }
  if (parts.FREQ === 'MONTHLY' && parts.BYMONTHDAY) {
    return { type: 'monthly', dayOfMonth: Number(parts.BYMONTHDAY), ...(until ? { until } : {}) };
  }
  return null;
}
